/**
 * BrowsePage Component
 *
 * @remarks
 * Fetches and renders a complete browse page with all rails and tiles.
 * Uses the fetchBrowsePage utility to orchestrate the data fetching flow.
 *
 * Supports two modes:
 * 1. Standard mode (enableLazyLoading=false): Loads all rails at once
 * 2. Lazy loading mode (enableLazyLoading=true): Loads rails progressively as user scrolls
 *
 * @example
 * Standard loading (all rails at once)
 * ```tsx
 * <BrowsePage slug="/home" fetchTileDetails={true} />
 * ```
 *
 * @example
 * Lazy loading mode (progressive loading)
 * ```tsx
 * <BrowsePage
 *   slug="/movies"
 *   enableLazyLoading={true}
 *   batchSize={3}           // Load 3 rails per batch
 *   initialBatchSize={5}     // Load 5 rails initially
 *   loadThreshold={800}      // Start loading 800px before scrolling to bottom
 *   skeletonTileCount={6}    // Show 6 skeleton tiles per rail
 * />
 * ```
 *
 *
 * Performance considerations:
 * - batchSize: Smaller values (3-5) improve initial load time but require more network requests
 * - initialBatchSize: Load more rails initially (5-7) for better perceived performance
 * - loadThreshold: Higher values (800-1200px) start loading earlier, providing smoother UX
 */

import { Fragment, useEffect, useState } from "react";
import { useAtom } from "jotai";

import { isModalOpenAtom, selectedAssetAtom } from "@/constants/atoms";
import { fetchBrowsePage } from "@/utils/FetchBrowsePage";
import {
  RailWithTiles,
  isGameRail,
  isShowcaseRail,
  isSkippedRail,
  hasError,
} from "@/utils/FetchBrowsePage/types";
import { useLazyLoadRails } from "@/utils/useLazyLoadRails";
import { renderSkeletonTiles } from "@/utils/RenderSkeletonTiles";
import { Rail } from "@/components/organisms/Rail";
import { Tile34 } from "@/components/molecules/Tile34";
import { AssetResponse } from "@/utils/FetchAssetDetails/types";
import { PeacockNodeType, TemplateType } from "@/utils/shared-atom-types";
import { MiniGameTile } from "@/components/molecules/MiniGameTile";
import { SecondaryNavTile } from "@/components/molecules/SecondaryNavTile";
import { Text } from "@/components/atoms/Text";
import { VideoPlayer } from "@/components/organisms/VideoPlayer";
import { getTileFormat } from "@/utils/GetTileFormat";
import { ShowcaseCarousel } from "@/components/organisms/ShowcaseCarousel";
import { RailVariant } from "@/components/organisms/Rail/types";
import { Spotlight } from "@/components/organisms/Spotlight";
import { useClassNames } from "@/utils/classNames";

import { BrowsePageProps } from "./types";
import styles from "./BrowsePage.module.scss";

/**
 * Check if a rail should render SecondaryNavTiles
 */
const isSecondaryNavRail = (rail: RailWithTiles): boolean =>
  rail.title.toLowerCase().includes("brand");

const BrowsePage = ({
  slug,
  fetchTileDetails = true,
  skipPersonalized = true,
  showMobileContent = true,
  enableLazyLoading = false,
  batchSize = 5,
  initialBatchSize,
  loadThreshold = 800,
  skeletonTileCount = 6,
  scrollContainerRef,
  className,
  ...props
}: BrowsePageProps) => {
  /** Use lazy loading hook if enabled */
  const lazyLoadResult = useLazyLoadRails({
    slug,
    batchSize,
    initialBatchSize,
    loadThreshold,
    fetchTileDetails,
    skipPersonalized,
    showMobileContent,
    scrollContainerRef,
  });

  /** Standard loading state */
  const [rails, setRails] = useState<RailWithTiles[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedAsset, setSelectedAsset] = useAtom(selectedAssetAtom);
  const [isModalOpen, setIsModalOpen] = useAtom(isModalOpenAtom);

  /** Fetch all rails at once if lazy loading is disabled */
  useEffect(() => {
    if (enableLazyLoading) return;

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const browsePageRails = await fetchBrowsePage(slug, {
          fetchTileDetails,
          skipPersonalized,
          showMobileContent,
        });

        setRails(browsePageRails);
      } catch (err) {
        /** Silently fail - errors are expected for restricted content */
        setError(
          err instanceof Error ? err.message : "Failed to fetch browse page",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [
    slug,
    fetchTileDetails,
    skipPersonalized,
    showMobileContent,
    enableLazyLoading,
  ]);

  /** Use lazy loading data if enabled */
  const displayRails = enableLazyLoading ? lazyLoadResult.rails : rails;
  const displayLoading = enableLazyLoading ? lazyLoadResult.loading : loading;
  const displayError = enableLazyLoading ? lazyLoadResult.error : error;
  const loadTriggerRef = enableLazyLoading
    ? lazyLoadResult.loadTriggerRef
    : undefined;
  const allRailsMetadata = enableLazyLoading
    ? lazyLoadResult.allRailsMetadata
    : [];

  /** When lazy loading, combine loaded rails with metadata-only rails */
  const railsToRender = enableLazyLoading
    ? allRailsMetadata.map((metadata) => {
        /** Find if this rail has been loaded with tiles */
        const loadedRail = displayRails?.find((r) => r.id === metadata.id);
        return loadedRail || metadata;
      })
    : displayRails;

  const handleTileClick = (asset: AssetResponse) => {
    setSelectedAsset(asset);
    setIsModalOpen(true);
  };

  const handleCloseVideoPlayer = () => {
    setIsModalOpen(false);
    setSelectedAsset(null);
  };

  const cn = useClassNames(styles, styles["browse-page"], className);

  /** Show skeleton rails during initial metadata load */
  if (displayLoading && (!railsToRender || railsToRender.length === 0)) {
    /** Use actual rail count if available, otherwise fall back to batch size */
    const skeletonCount =
      allRailsMetadata.length > 0
        ? allRailsMetadata.length
        : initialBatchSize || batchSize;

    return (
      <div className={cn} {...props}>
        {Array.from({ length: skeletonCount }).map((_, railIndex) => {
          const railMetadata = allRailsMetadata[railIndex];
          return (
            <Fragment key={`initial-skeleton-rail-${railIndex}`}>
              <Rail title={railMetadata?.title || ""}>
                {renderSkeletonTiles(railMetadata, skeletonTileCount)}
              </Rail>
            </Fragment>
          );
        })}
      </div>
    );
  }

  if (!railsToRender || railsToRender.length === 0) {
    return (
      <div className={cn} {...props}>
        <div className={styles["browse-page__loading"]}>No rails found</div>
      </div>
    );
  }

  /** Find index of first rail without tiles to attach load trigger observer */
  const loadTriggerRailIndex = enableLazyLoading
    ? railsToRender.findIndex((rail) => !rail.tiles || rail.tiles.length === 0)
    : -1;

  return (
    <div className={cn} {...props}>
      {railsToRender.map((rail, railIndex) => {
        /** Skip rails that were marked as skipped (e.g., personalized rails) */
        if (isSkippedRail(rail)) {
          return null;
        }

        /** Skip rails with errors */
        if (hasError(rail)) {
          return null;
        }

        /** For SHOWCASE template rails, use original tiles to preserve correct artwork */
        /** For other rails, use tilesWithDetails if available, otherwise fall back to tiles */
        const isShowcase = isShowcaseRail(rail);
        let tilesToRender = rail.tiles;

        /** Check if this rail has loaded tiles yet */
        const hasTiles = tilesToRender && tilesToRender.length > 0;

        /** Check if this rail should have the load trigger observer attached */
        const isLoadTrigger = railIndex === loadTriggerRailIndex;

        /** If no tiles loaded yet and lazy loading is enabled, show skeleton */
        if (enableLazyLoading && !hasTiles) {
          return (
            <div key={rail.id} ref={isLoadTrigger ? loadTriggerRef : undefined}>
              <Rail title={rail.title}>
                {renderSkeletonTiles(rail, skeletonTileCount)}
              </Rail>
            </div>
          );
        }

        /** Filter out LINEAR_SLOT tiles */
        tilesToRender = tilesToRender.filter(
          (tile: AssetResponse) => tile.type !== PeacockNodeType.LinearSlot,
        );

        /** Skip empty rails (after filtering) */
        if (!tilesToRender || tilesToRender.length === 0) {
          return null;
        }

        if (rail.renderHint.template === TemplateType.Highlight) {
          return (
            <Rail variant={RailVariant.ShowcaseCarousel} key={rail.id} snap>
              {tilesToRender.map((tile: AssetResponse, index: number) => (
                <ShowcaseCarousel
                  key={tile.id || `showcase-${index}`}
                  asset={tile}
                  format={getTileFormat(tile)}
                />
              ))}
            </Rail>
          );
        }

        /** Render Spotlight for SHOWCASE template with a single asset */
        if (isShowcase && tilesToRender.length === 1) {
          const spotlightAsset = tilesToRender[0];
          return (
            <Spotlight
              key={rail.id}
              asset={spotlightAsset}
              title={rail.title}
              scrollContainerRef={scrollContainerRef}
            />
          );
        }

        /** Render standard rail for all other cases */
        return (
          <Rail title={rail.title} key={rail.id}>
            {tilesToRender.map((tile: AssetResponse, index: number) => {
              /** Determine the tile format based on asset type and rail render hints */
              if (isSecondaryNavRail(rail)) {
                return (
                  <SecondaryNavTile
                    key={tile.id || `tile-${index}`}
                    asset={tile}
                  />
                );
              }

              if (isGameRail(rail)) {
                return (
                  <MiniGameTile key={tile.id || `tile-${index}`} asset={tile} />
                );
              }

              const format = getTileFormat(tile, rail.renderHint);

              return (
                <Tile34
                  key={tile.id || `tile-${index}`}
                  asset={tile}
                  format={format}
                  onTileClick={handleTileClick}
                />
              );
            })}
          </Rail>
        );
      })}

      {/* Show error if any */}
      {displayError && <Text variant="p">Error: {displayError}</Text>}

      {selectedAsset && (
        <VideoPlayer
          isModal={true}
          onClose={handleCloseVideoPlayer}
          showControls={true}
          skipSeconds={10}
          videoProps={{
            cloudflare: false,
            src: "https://firebasestorage.googleapis.com/v0/b/peacock-design-eng.firebasestorage.app/o/default.mp4?alt=media&token=dd319fad-6b22-413d-b550-94c64959984b",
            controls: false,
            muted: false,
            playsInline: true,
            preload: "auto",
          }}
        />
      )}
    </div>
  );
};

export { BrowsePage };
